import React, { useEffect, useState } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi, bankAccountsApi } from "../../services/api";
import GenericReportViewer from "./GenericReportViewer";
import { dateForPeriod } from "./reportUtils";

interface Props {
  period: string;
}

const BankReconciliationReport: React.FC<Props> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [accounts, setAccounts] = useState<any[]>([]);
  const [bankAccountId, setBankAccountId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!currentBuilding?.id) return;
    let cancelled = false;
    bankAccountsApi
      .getAll(currentBuilding.id)
      .then((items: any[]) => {
        if (cancelled) return;
        setAccounts(items || []);
        if (items?.length && !bankAccountId) setBankAccountId(items[0].id);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id]);

  useEffect(() => {
    if (!currentBuilding?.id || !bankAccountId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await reportsApi.getBankReconciliation(
          currentBuilding.id,
          {
            bankAccountId,
            date: dateForPeriod(period),
          },
        );
        if (!cancelled) setReport(data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError("Failed to load bank reconciliation.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, bankAccountId, period]);

  return (
    <div>
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <label className="block text-xs uppercase text-gray-500 mb-1">
          Bank account
        </label>
        <select
          value={bankAccountId}
          onChange={(e) => setBankAccountId(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        >
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name || a.accountName || a.id}
            </option>
          ))}
        </select>
      </div>
      <GenericReportViewer
        title="Bank Reconciliation"
        loading={loading}
        error={error}
        report={report}
      />
    </div>
  );
};

export default BankReconciliationReport;
